import { faMobileSignalOut } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { formatDateTime } from '@pickleballinc/lib/format-dates';
import clsx from 'clsx';
import { useState } from 'react';

import { HistoryInfoModal } from '@/modules/results/components/match/HistoryInfoModal';
import { InfoModal } from '@/modules/results/components/match/InfoModal';
import { getMatchTypeText } from '@/modules/results/helpers';
import { MatchPropsShort, PlayerMatchesHistoryInfoModalArgs } from '@/modules/results/types';
import { useAuth } from '@/permissions/AuthProvider';
import { Permission } from '@/permissions/permission';
import { shouldShowMatchStartTimeResults } from '@/utils/helpers/shouldShowMatchStartTime';

interface MatchInfoProps {
  match: MatchPropsShort;
  isHistory?: boolean;
  prevMatchesIds?: string[] | null;
  playerMatchesArgs?: PlayerMatchesHistoryInfoModalArgs;
  shouldFetchPrevMatches?: boolean;
}

export function MatchInfo({
  match,
  isHistory,
  prevMatchesIds,
  playerMatchesArgs,
  shouldFetchPrevMatches,
}: MatchInfoProps) {
  const { hasPermission } = useAuth();
  const isSuperAdmin = hasPermission(Permission.SUPER_ADMIN);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const utcOffset = match?.utc_offset;
  const abbreviation = match?.timezone_abbreviation;
  const team_one_player_one_name = match.team_one_player_one_name;
  const team_one_player_two_name = match.team_one_player_two_name;
  const team_two_player_one_name = match.team_two_player_one_name;
  const team_two_player_two_name = match.team_two_player_two_name;
  const showTime = shouldShowMatchStartTimeResults(match);

  const checkNames = () => {
    if (match.format_title === 'Doubles') {
      if (
        team_one_player_one_name === '' ||
        team_one_player_two_name === '' ||
        team_two_player_one_name === '' ||
        team_two_player_two_name === ''
      ) {
        return false;
      } else {
        return true;
      }
    } else {
      if (team_one_player_one_name === '' || team_two_player_one_name === '') {
        return false;
      } else {
        return true;
      }
    }
  };

  const local_date_match_planned_start = match.match_planned_start;
  const local_date_match_completed = match.match_start;

  const matchStartDateUTC = formatDateTime(local_date_match_planned_start, {
    timezoneAbbreviation: abbreviation,
    utcOffset: utcOffset,
    type: 'time',
  });
  const currentUTCDate = formatDateTime(local_date_match_completed, {
    timezoneAbbreviation: abbreviation,
    utcOffset: utcOffset,
    type: 'time',
  });

  const handleModalClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setIsModalOpen(true);
  };

  const handleModalClose: React.MouseEventHandler = (e) => {
    e.stopPropagation();
    setIsModalOpen(false);
  };

  const matchInfoBox = clsx('flex min-w-[130px] flex-col border-t py-2 px-1 sm:min-w-[192px]', {
    'sm:items-center sm:justify-evenly sm:border-t-0 h-full': !isHistory,
  });

  const matchTypeText = getMatchTypeText(
    match.round_text,
    match.round_number,
    match.in_bracket_type
  );

  return (
    <div className={matchInfoBox}>
      <div>
        <span>{matchTypeText}</span>
      </div>
      {match.match_status === 2 ? (
        <div className='flex flex-col gap-1 sm:items-center'>
          <span className='text-sm sm:text-base'>Match Started</span>
          <span className=''>{currentUTCDate}</span>
        </div>
      ) : match.match_status === 1 ? (
        <div className='flex flex-col gap-1 sm:items-center'>
          <span className='text-xs sm:text-sm'>Upcoming</span>
          {checkNames() && showTime ? (
            <span className='text-sm font-semibold sm:text-base'>{matchStartDateUTC}</span>
          ) : null}
        </div>
      ) : match.match_status === 4 ? (
        <div>
          <span className='text-sm sm:text-base'>Match completed</span>
        </div>
      ) : null}
      <div className='mt-2 flex items-center gap-1'>
        {match.is_consumed_by_referee_app && checkNames() ? (
          <FontAwesomeIcon color='#572bd7' className='mr-1 text-xl' icon={faMobileSignalOut} />
        ) : null}
        {checkNames() && (
          <a href={`/results/match/${match.match_uuid}`}>
            <span className='cursor-pointer text-sm text-[#572bd7] underline sm:text-base'>
              See details
            </span>
          </a>
        )}
        {isSuperAdmin ? (
          <button onClick={handleModalClick} className='ml-2'>
            <span className='flex size-4 cursor-pointer items-center justify-center rounded-full border border-gray-900 text-xs text-[#572bd7]'>
              !
            </span>
          </button>
        ) : null}
      </div>
      {isHistory ? (
        <HistoryInfoModal
          isOpen={isModalOpen}
          onClose={handleModalClose}
          isSuperAdmin={isSuperAdmin}
          matchUuid={match.match_uuid}
          shouldFetch={shouldFetchPrevMatches}
          prevMatchesUuids={prevMatchesIds}
          playerMatchesArgs={playerMatchesArgs}
        />
      ) : (
        <InfoModal
          isOpen={isModalOpen}
          onClose={handleModalClose}
          isSuperAdmin={isSuperAdmin}
          matchUuid={match.match_uuid}
        />
      )}
    </div>
  );
}
